import React, { useState, useEffect } from 'react';
import { api } from '../shared/api';
import { useToast, useConfirm } from '../shared/AppDialogs';

/**
 * CallQueuePage - Live view of scheduled and active calls
 */
export function CallQueuePage({ agents }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [queue, setQueue] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, scheduled, active, completed

  // Load call queue
  useEffect(() => {
    loadQueue();
    // Refresh every 15 seconds
    const interval = setInterval(loadQueue, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadQueue = async () => {
    try {
      const data = await api.get('/call-queue');
      if (data.success) {
        setQueue(data.scheduled || []);
        setRecentCalls(data.recent || []);
      }
    } catch (err) {

    } finally {
      setLoading(false);
    }
  };

  const cancelCall = async (callId) => {
    const ok = await confirm({ title: 'Anruf abbrechen', message: 'Diesen geplanten Anruf wirklich abbrechen?', confirmText: 'Abbrechen', type: 'danger' }); if (!ok) return;
    
    try {
      await api.post(`/call-queue/${callId}/cancel`);
      loadQueue();
    } catch (err) {

    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">⏳ Geplant</span>;
      case 'calling':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full animate-pulse">📞 Ruft an...</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full animate-pulse">🔊 Im Gespräch</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">✅ Abgeschlossen</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">❌ Fehlgeschlagen</span>;
      case 'not_reached':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">📵 Nicht erreicht</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">{status}</span>;
    }
  };

  const getOutcomeBadge = (outcome) => {
    if (!outcome) return null;
    const colors = {
      confirmed: 'bg-green-100 text-green-700',
      rescheduled: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      not_interested: 'bg-gray-100 text-gray-600',
      callback: 'bg-amber-100 text-amber-700',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[outcome.id] || 'bg-gray-100 text-gray-600'}`}>
        {outcome.label}
      </span>
    );
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Heute';
    if (date.toDateString() === tomorrow.toDateString()) return 'Morgen';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">📞 Anruf-Warteschlange</h1>
          <p className="text-gray-600">Live-Übersicht aller geplanten und aktiven Anrufe</p>
        </div>
        <button
          onClick={loadQueue}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
        >
          🔄 Aktualisieren
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-3xl font-bold text-blue-600">{queue.filter(c => c.status === 'scheduled').length}</div>
          <div className="text-sm text-gray-500">Geplant</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-3xl font-bold text-amber-600">{queue.filter(c => c.status === 'calling' || c.status === 'in_progress').length}</div>
          <div className="text-sm text-gray-500">Aktiv</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-3xl font-bold text-green-600">{recentCalls.filter(c => c.outcome?.id === 'confirmed').length}</div>
          <div className="text-sm text-gray-500">Heute erfolgreich</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-3xl font-bold text-gray-600">{recentCalls.length}</div>
          <div className="text-sm text-gray-500">Heute gesamt</div>
        </div>
      </div>

      {/* Scheduled Calls */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">⏳ Geplante Anrufe</h2>
          <span className="text-sm text-gray-500">{queue.length} in der Warteschlange</span>
        </div>
        
        {queue.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {queue.map((call) => (
              <div key={call.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Time */}
                  <div className="text-center min-w-[60px]">
                    <div className="text-lg font-semibold text-gray-800">{formatTime(call.scheduled_at)}</div>
                    <div className="text-xs text-gray-500">{formatDate(call.scheduled_at)}</div>
                  </div>
                  
                  {/* Contact Info */}
                  <div>
                    <div className="font-medium text-gray-800">{call.contact_name || call.phone_number}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <span>📞 {call.phone_number}</span>
                      <span>•</span>
                      <span>🤖 {call.agent_name}</span>
                      <span>•</span>
                      <span>📤 {call.campaign_name}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {getStatusBadge(call.status)}
                  <button
                    onClick={() => cancelCall(call.id)}
                    className="text-red-500 hover:bg-red-50 p-2 rounded-lg text-sm"
                    title="Abbrechen"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">📭</div>
            <p>Keine geplanten Anrufe</p>
          </div>
        )}
      </div>

      {/* Recent Calls */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">📋 Letzte Anrufe</h2>
          <span className="text-sm text-gray-500">Heute</span>
        </div>
        
        {recentCalls.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {recentCalls.map((call) => (
              <div key={call.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Time */}
                  <div className="text-center min-w-[60px]">
                    <div className="text-lg font-semibold text-gray-800">{formatTime(call.completed_at || call.started_at)}</div>
                  </div>
                  
                  {/* Contact Info */}
                  <div>
                    <div className="font-medium text-gray-800">{call.contact_name || call.phone_number}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <span>📞 {call.phone_number}</span>
                      {call.duration && <><span>•</span><span>⏱️ {Math.floor(call.duration / 60)}:{(call.duration % 60).toString().padStart(2, '0')}</span></>}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {getOutcomeBadge(call.outcome)}
                  {getStatusBadge(call.status)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">📭</div>
            <p>Noch keine Anrufe heute</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CallQueuePage;
